<script setup lang="ts">
import { useCompaniesStore } from "~/store/companies/companies.store";
import TagDropdown from "../Dropdowns/TagDropdown.vue";
import RadioInput from "../Inputs/RadioInput.vue";
import { VisibleStatus } from "~/utils/enums/training.enums";
import { useAuthStore } from "~/store/auth/auth.store";
import SvgIcon from "../SvgIcon.vue";
import Tooltip from "../Tooltip.vue";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { GroupsApi } from "@simbelapp/order-sdk";
import { useFeedback } from "~/composables/feedback/useFeedback";

const props = defineProps({
  modelValue: Object,
  readonly: Boolean,
  validationErrors: Boolean,
});

const emit = defineEmits(["update:modelValue", "resetValidation"]);
const groupsContainer = ref<HTMLElement | null>(null);
const companiesStore = useCompaniesStore();
const authStore = useAuthStore();

// Groups are now stored as full objects {groupId, name} in modelValue
const groups = computed(() => props.modelValue?.groups || []);

// A click on the already-checked option (its text included) must not drop the picked groups.
const updateVisibility = (value: VisibleStatus) => {
  if (props.modelValue?.visible === value) return;
  emit("update:modelValue", { visible: value, groups: [] });

  if (value === VisibleStatus.GROUPS) {
    nextTick(() => {
      groupsContainer.value?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  } else {
    emit("resetValidation");
  }
};

const updateGroups = (groups: any[]) => {
  // Keep full group objects {groupId, name} for display purposes
  const groupObjects = groups.map((g) => ({ groupId: g.groupId, name: g.name }));
  emit("update:modelValue", { visible: VisibleStatus.GROUPS, groups: groupObjects });
};

const servicesAPI = async (params: any) => {
  const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, GroupsApi);
  const feedback = useFeedback();
  try {
    const data = await apiInstance.getAvailableGroupsByRoles({ lazyLoad: true, enableRolesFilters: true, ...params });
    if (data) {
      return data;
    }
  } catch {
    feedback.error("Une erreur est survenue", "small");
  }
};
</script>

<template>
  <div>
    <div v-if="modelValue" class="flex flex-col gap-2 mt-2">
      <label class="flex gap-2 w-fit" :class="readonly ? 'cursor-not-allowed' : 'cursor-pointer'">
        <RadioInput
          v-model="modelValue.visible"
          class="min-w-[16px] min-h-[16px]"
          :checked="modelValue.visible === VisibleStatus.HIDDEN"
          :disabled="readonly"
          :value="VisibleStatus.HIDDEN"
          @click="updateVisibility(VisibleStatus.HIDDEN)"
        />
        <span class="text-secondary-color body_regular">Visible pour personne</span>
      </label>
      <label class="flex gap-2 w-fit" :class="readonly ? 'cursor-not-allowed' : 'cursor-pointer'">
        <RadioInput
          v-model="modelValue.visible"
          class="min-w-[16px] min-h-[16px]"
          :checked="modelValue.visible === VisibleStatus.ALL"
          :disabled="readonly"
          :value="VisibleStatus.ALL"
          @click="updateVisibility(VisibleStatus.ALL)"
        />
        <span class="text-secondary-color body_regular">Visible pour tous</span>
      </label>

      <label class="flex gap-2 w-fit" :class="readonly ? 'cursor-not-allowed' : 'cursor-pointer'">
        <RadioInput
          v-model="modelValue.visible"
          class="min-w-[16px] min-h-[16px]"
          :checked="modelValue.visible === VisibleStatus.GROUPS"
          :disabled="readonly"
          :value="VisibleStatus.GROUPS"
          @click="updateVisibility(VisibleStatus.GROUPS)"
        />
        <span class="text-secondary-color body_regular">Visible pour certains groupes</span>
      </label>
    </div>
    <transition>
      <div v-if="modelValue && modelValue.visible === VisibleStatus.GROUPS" class="flex flex-col mt-4 ml-6 gap-2">
        <div class="flex justify-between items-center">
          <div class="flex items-center">
            <span class="text-secondary-color body_regular">Sélectionnez un ou plusieurs groupe(s) *</span>
            <Tooltip v-if="!authStore.isCompanyAdmin" :position="'top-right'" class="ml-1">
              <template #content>
                <div class="flex w-[300px] gap-[8px]">
                  <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                  <span> Contactez votre administrateur pour paramétrer les groupes </span>
                </div>
              </template>
            </Tooltip>
          </div>
          <div
            v-if="authStore.isCompanyAdmin"
            class="cursor-pointer button_secondary_default"
            @click="navigateTo('settings?section=groups', { open: { target: '_blank' } })"
          >
            <span class="body_regular">Paramétrer</span>
            <SvgIcon name="link" class="w-[14px] h-[14px]"></SvgIcon>
          </div>
        </div>
        <TagDropdown
          v-model="groups"
          class="w-full"
          :loading="!companiesStore.companyGroups"
          :options="companiesStore.companyGroups!"
          placeholder="Choisissez une option"
          label="name"
          track-by="groupId"
          :disabled="readonly"
          :error="validationErrors"
          :store="companiesStore"
          store-object="companyGroups"
          :services-a-p-i="servicesAPI"
          :limiter="3"
          @update:model-value="updateGroups"
        >
          <template #tag="{ option }">
            <div class="w-full whitespace-break-spaces py-1 leading-normal">{{ option.name }}</div>
          </template>
          <template #option="{ option }">
            <span class="w-full whitespace-break-spaces py-1 leading-normal">{{ option.name }} </span>
          </template>
        </TagDropdown>
        <span v-if="validationErrors" class="text-primary-red-color body_regular"
          >Veuillez sélectionner au moins un groupe</span
        >
      </div>
    </transition>
  </div>
  <div ref="groupsContainer"></div>
</template>
